import { ABP, ListService, PagedResultDto, TrackByService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { filter, switchMap } from 'rxjs/operators';
import { GetQueueItemsInput, QueueItemDto } from '@proxy/queue-service/queue';
import { QueueService } from '@proxy/queue-service/queue/queue.service';
import { SelectionType } from '@swimlane/ngx-datatable';
import { AssetService } from '@register/proxy/register-service/assets';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { State } from '@/store/models/state.model';
import { UpdateQueueSizeAction } from '@/store/actions/queue.actions';
import { SiteService } from '@proxy/register-service/sites';
import { AssetDetailsDto } from '@proxy/register-service/assets';
import { LocationDefinition } from '@register/proxy/register-service/locations';
import { LocationTreeNode } from '@/locations/models/location-tree-node.model';

@Component({
  selector: 'app-asset-queue',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
  templateUrl: './queue.component.html',
  styleUrls: ['./queue.component.scss'],
})
export class QueueComponent implements OnInit {
  data: PagedResultDto<QueueItemDto> = {
    items: [],
    totalCount: 0,
  };

  filters = {} as GetQueueItemsInput;
  form: FormGroup;
  isFiltersHidden = true;
  isBusy = false;
  selected: QueueItemDto[] = [];
  dataHistory: QueueItemDto[] = [];
  isModalOpen = false;
  pendingPageChange = false;
  isAssetActive: boolean = false;
  modalType: string = '';
  SelectionType = SelectionType;
  convertedData: AssetDetailsDto[];
  convertedSelectedData: string[];
  categoryName: string;
  locationDefinition = LocationDefinition;
  homeLocationName: string;
  secondaryLocationName: string;
  jobsiteLocationName: string;
  locations: string[] = [];
  topLevels: string[] = [];
  appendInProgress = false;

  constructor(
    public readonly list: ListService,
    public readonly track: TrackByService,
    public readonly service: QueueService,
    public readonly siteService: SiteService,
    public readonly assetService: AssetService,
    private confirmation: ConfirmationService,
    private router: Router,
    private store: Store<State>,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.list.maxResultCount = 200;
    const getData = (query: ABP.PageQueryParams) =>
      this.service.getList({
        ...query,
        ...this.filters,
        term: query.filter,
        assetManufacturerIdTwo: this.filters.assetManufacturerIdOne,
      });
    const setData = (list: PagedResultDto<QueueItemDto>) => {
      this.data = list;
      this.store.dispatch(new UpdateQueueSizeAction(this.data.totalCount));
      this.convertedData = this.data.items.map(this.convertToAssetDetails);
      this.clearSelection();

      this.selectAllRows(true);
      if (this.pendingPageChange) {
        this.pendingPageChange = false;
        let newSelected = [...this.selected];

        if (!this.data.items.some(e => this.selected.some(sel => sel.id === e.id))) {
          newSelected = newSelected.concat(
            this.data.items.filter(e => this.locations.includes(e.homeLocationId)),
          );
        }

        this.selected = newSelected.filter(
          (item, index, self) => index === self.findIndex(i => i.id === item.id),
        );
      }
    };

    this.list.hookToQuery(getData).subscribe(setData);
    this.store.dispatch(new UpdateQueueSizeAction(this.data.totalCount));
  }

  private clearSelection() {
    this.selected = [];
    this.locations = [];
    this.topLevels = [];
  }

  private convertToAssetDetails(item: QueueItemDto): AssetDetailsDto {
    return {
      id: item.assetId,
      categoryFullName: '',
      categoryName: '',
      categoryNickname: '',
      categoryPath: '',
      categoryPictureUrl: '',
      categoryId: '',
      smartCodeUrl: '',
      isSmartCodeChanged: true,
      code: item.assetCode,
      isActive: item.isActive,
      isFixed: null,
      assetType: null,
      siteId: '',
      homeLocationId: item.homeLocationId,
      homeLocationName: item.homeLocationName,
      secondaryLocationId: item.secondaryLocationId,
      secondaryLocationName: item.secondaryLocationName,
      jobsiteGroupIds: item.jobsiteGroupIds,
      jobsiteIds: item.jobsiteIds,
      jobsiteId: item.jobsiteId,
      jobsiteNames: item.jobsiteNames,
      assetIsRecalled: item.assetIsRecalled,
      primaryIdPrefix: item.assetPrimaryIDPrefix,
    };
  }

  onInspect(record: QueueItemDto) {
    this.router.navigate(['/inspections/create'], { queryParams: { assetId: record.assetId } });
  }

  isSelected(row: QueueItemDto): boolean {
    return this.selected.some(e => e.id === row.id);
  }

  isAllRowsSelected(): boolean {
    if (!this.data.items.length || !this.selected.length) {
      return false;
    }
    return this.data.items.every(item => this.selected.some(sel => sel.id === item.id));
  }

  selectAllRows(select: boolean): void {
    if (select) {
      const allItems = this.data.items.filter(
        item => !this.selected.some(sel => sel.id === item.id),
      );
      this.selected = [...this.selected, ...allItems];
    } else {
      this.selected = this.selected.filter(
        sel => !this.data.items.some(item => item.id === sel.id),
      );
    }
  }

  isTopLevel(row: QueueItemDto): boolean {
    return row.homeLocationName?.split(',').length === 1;
  }

  onAssetStateChange(ids: string[], state?: boolean) {
    this.assetService.changeState({ ids: ids, isActive: state }).subscribe(p => p);
  }

  selectParents(event: any, name: string, id: string, groupId: string) {
    const filteredItems = this.data.items.filter(
      e => e.homeLocationName?.split(',')[0] === name.split(',')[0],
    );
    const selectedLocationIds = filteredItems.map(e => e.homeLocationId);
    if (event.target.checked) {
      this.topLevels = [...this.topLevels, groupId || id];
      this.locations = [...this.locations, ...selectedLocationIds];
      this.selected = [...this.selected, ...filteredItems].filter(
        (asset, index, self) => index === self.findIndex(a => a.id === asset.id),
      );
    } else {
      this.topLevels = [...this.topLevels].filter(e => e !== id && e !== groupId);
      this.locations = [...this.locations].filter(e => !selectedLocationIds.includes(e));
      this.selected = [...this.selected].filter(
        e => e.homeLocationName?.split(',')[0] !== name.split(',')[0],
      );
    }
  }

  onMakeActive() {
    const ids = this.selected.map(asset => asset.assetId);

    this.confirmation
      .warn('QueueService::ActivateConfirmationMessage', 'QueueService::AreYouSure', {
        messageLocalizationParams: [ids.length.toString()],
      })
      .pipe(
        filter(status => status === Confirmation.Status.confirm),
        switchMap(() => this.assetService.changeState({ ids: ids, isActive: true })),
      )
      .subscribe(
        () => {},
        error => {
          console.error('Error activating assets', error);
        },
      );
  }

  onMakeInActive() {
    const ids = this.selected.map(asset => asset.assetId);
    this.confirmation
      .warn('QueueService::InactivateConfirmationMessage', 'QueueService::AreYouSure', {
        messageLocalizationParams: [ids.length.toString()],
      })
      .pipe(
        filter(status => status === Confirmation.Status.confirm),
        switchMap(() => this.assetService.changeState({ ids: ids, isActive: false })),
      )
      .subscribe(
        () => {},
        error => {
          console.error('Error activating assets', error);
        },
      );
  }

  onModalOpen(modalType: string) {
    const hasParentOrComponents = this.selected.some(e => e.isParent || e.components != null);
    if (hasParentOrComponents) {
      this.confirmation.error('QueueService::BulkMoveMessage', 'QueueService::BulkAction', {
        cancelText: 'OK',
        hideYesBtn: true,
      });
      return;
    }
    this.convertedSelectedData = this.selected.map(e => e.assetId);

    this.modalType = modalType;
    this.isModalOpen = true;
  }

  resetData() {
    this.selected = [];
    this.dataHistory = [];
    this.locations = [];
    this.topLevels = [];
  }

  onModalClose() {
    this.modalType = '';
    this.isModalOpen = false;
    this.resetData();

    this.list.get();
  }

  onDequeue(record?: QueueItemDto) {
    this.confirmation
      .warn('QueueService::DeleteConfirmationMessage', 'QueueService::AreYouSure', {
        messageLocalizationParams: [],
      })
      .pipe(
        filter(status => status === Confirmation.Status.confirm),
        switchMap(() =>
          record
            ? this.service.dequeue(record.id)
            : this.service.clear(this.selected.map(x => x.id)),
        ),
      )
      .subscribe(() => {
        this.list.get();
        this.store.dispatch(new UpdateQueueSizeAction(this.data.totalCount - 1));
        this.cdr.detectChanges();
      });
  }

  onClear() {
    this.confirmation
      .warn('QueueService::DeleteConfirmationMessage', 'QueueService::AreYouSure', {
        messageLocalizationParams: [],
      })
      .pipe(
        filter(status => status === Confirmation.Status.confirm),
        switchMap(() => this.service.clear([])),
      )
      .subscribe(() => {
        this.list.get();
        this.store.dispatch(new UpdateQueueSizeAction(0));
      });
  }

  onPageChange() {
    this.dataHistory = [...this.dataHistory, ...this.data.items].filter(
      (asset, index, self) => index === self.findIndex(a => a.id === asset.id),
    );

    this.pendingPageChange = true;
  }

  onSelectSubAssets(event: any, item: QueueItemDto) {
    const index = this.selected.findIndex(subAsset => subAsset.id === item.id);

    if (event.target.checked) {
      if (index === -1) {
        this.selected.push(item);
      }
    } else {
      if (index !== -1) {
        this.selected.splice(index, 1);
        if (!this.selected) {
          this.resetData();
          return;
        }
        if (this.locations.length) {
          const ids = this.selected.map(e => e.homeLocationId);
          this.locations = [...this.locations].filter(e => ids.includes(e));
        }
      }
    }
  }

  inspectAll() {
    const allAssetsInQueue = this.data.items;

    this.router.navigate(['/inspections/create'], {
      queryParams: { selected: allAssetsInQueue },
    });
  }

  onView(asset: QueueItemDto) {
    this.router.navigate(['/assets/view/' + asset.assetId], {});
  }

  clearFilters() {
    this.filters = {} as GetQueueItemsInput;
    this.clearLocationFilters();
    this.categoryName = null;

    this.list.get();
  }

  setLocationId(location: LocationTreeNode) {
    if (location) {
      switch (location.locationDefinition) {
        case this.locationDefinition.HOME:
          this.filters.homeLocationId = location.id;
          break;
        case this.locationDefinition.SECONDARY:
          this.filters.secondaryLocationId = location.id;
          break;
        case this.locationDefinition.JOBSITE:
          this.filters.jobsiteLocationId = location.id;
          break;
      }
      this.setLocationName(location.locationDefinition, location.name);
    } else {
      this.clearLocationFilters(location.locationDefinition);
    }

    this.list.get();
  }

  setCategoryFilter(event) {
    this.filters.categoryPath = event?.path;
    this.categoryName = event?.fullName;

    this.list.get();
  }

  private setLocationName(locationDefinition: LocationDefinition, name: string) {
    switch (locationDefinition) {
      case this.locationDefinition.HOME:
        this.homeLocationName = name;
        break;
      case this.locationDefinition.SECONDARY:
        this.secondaryLocationName = name;
        break;
      case this.locationDefinition.JOBSITE:
        this.jobsiteLocationName = name;
        break;
    }
  }

  private clearLocationFilters(definition: LocationDefinition = null) {
    this.homeLocationName = null;
    this.secondaryLocationName = null;
    this.jobsiteLocationName = null;
    switch (definition) {
      case this.locationDefinition.HOME:
        this.filters.homeLocationId = null;
        break;

      case this.locationDefinition.SECONDARY:
        this.filters.secondaryLocationId = null;
        break;

      case this.locationDefinition.JOBSITE:
        this.filters.jobsiteLocationId = null;
        break;

      default:
        this.filters.homeLocationId = null;
        this.filters.secondaryLocationId = null;
        this.filters.jobsiteLocationId = null;
        break;
    }
  }

  deallocationButtonAvailability() {
    let foundDifference = false;
    let allEmpty = true;

    for (let index = 1; index < this.selected.length; index++) {
      const currentJobsites = JSON.stringify(this.selected[index].jobsiteNames);
      const previousJobsites = JSON.stringify(this.selected[index - 1].jobsiteNames);

      if (currentJobsites !== previousJobsites) {
        foundDifference = true;
        break;
      }
    }

    if (!foundDifference) {
      for (const item of this.selected) {
        if (item.jobsiteNames && item.jobsiteNames.length > 0) {
          allEmpty = false;
          break;
        }
      }
    }

    if (!allEmpty) {
      if (this.selected.length === 0) {
        allEmpty = true;
      }
    }

    const disableButton = this.selected.every(item => item.isDeallocated);

    return foundDifference || allEmpty || disableButton;
  }
  hasMore(): boolean {
    return (this.data?.items?.length || 0) < (this.data?.totalCount || 0);
  }

  showMore() {
    if (this.isBusy || this.appendInProgress || !this.hasMore()) return;
    this.appendInProgress = true;
    const skip = this.data.items.length;
    const take = this.list.maxResultCount || 10;
    const request: GetQueueItemsInput = {
      ...this.filters,
      term: this.list.filter,
      assetManufacturerIdTwo: this.filters.assetManufacturerIdOne,
      skipCount: skip,
      maxResultCount: take,
    };

    this.service.getList(request).subscribe({
      next: next => {
        const wasAllSelectedBefore = this.isAllRowsSelected();
        const previousItems = this.data.items;
        const merged = [...previousItems, ...next.items].filter(
          (item, index, self) => index === self.findIndex(a => a.id === item.id),
        );
        this.data = { items: merged, totalCount: next.totalCount };
        this.store.dispatch(new UpdateQueueSizeAction(this.data.totalCount));
        this.appendInProgress = false;
        if (wasAllSelectedBefore) {
          // Re-select all (adds any new ones)
          this.selectAllRows(true);
        }
      },
      error: err => {
        this.appendInProgress = false;
        // Optional: surface error via modal
        this.confirmation.error(
          err?.error?.message || err?.message || 'QueueService::UnexpectedError',
          'AbpUi::Error',
          { hideYesBtn: true, cancelText: 'OK' },
        );
      },
    });
  }
}
